import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus, FaTrash, FaHardHat, FaCheckCircle } from 'react-icons/fa';
import { getApiEndpoint } from '../../../../config/api';
import { getToken } from '../../../../utils/auth';

const SECTION_LABELS = {
  overview: 'Overview',
  fire: 'Fire Safety',
  roof: 'Roof Safety',
  staff: 'Staff Safety',
  equipment: 'Equipment',
  electrical: 'Electrical',
  building: 'Building Maintenance',
  emergency: 'Emergency',
  management: 'Management',
  documents: 'Documents',
  inspections: 'Inspections',
  tasks: 'Tasks',
  profile: 'Profile',
  settings: 'Settings'
};

function ContractorsSettings() {
  const { id: propertyId } = useParams();
  const location = useLocation();
  const propertyIdFromUrl = location.pathname.match(/\/propertydashboard\/(\d+)\/settings/)?.[1]
    || location.pathname.match(/\/properties\/(\d+)\/settings/)?.[1];
  const effectivePropertyId = (propertyId || propertyIdFromUrl) ? parseInt(propertyId || propertyIdFromUrl) : null;

  const [property, setProperty] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const buildHeaders = useCallback(() => {
    const t = getToken();
    return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  }, []);

  const fetchProperty = useCallback(async () => {
    if (!effectivePropertyId) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${effectivePropertyId}`), { headers: buildHeaders() });
      if (res.ok) setProperty(await res.json());
    } catch (e) {
      console.error('Failed to fetch property:', e);
    }
  }, [effectivePropertyId, buildHeaders]);

  const fetchContractors = useCallback(async () => {
    if (!effectivePropertyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${effectivePropertyId}/contractors`),
        { headers: buildHeaders() }
      );
      if (res.ok) setContractors(await res.json());
    } catch (e) {
      console.error('Failed to fetch contractors:', e);
    } finally {
      setLoading(false);
    }
  }, [effectivePropertyId, buildHeaders]);

  useEffect(() => { fetchProperty(); }, [fetchProperty]);
  useEffect(() => { fetchContractors(); }, [fetchContractors]);

  const handleRemove = async (linkId, name) => {
    if (!window.confirm(`Remove ${name} from this property?`)) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${effectivePropertyId}/contractors/${linkId}`), {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (res.ok) {
        toast.success('Contractor removed');
        fetchContractors();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || data.message || 'Failed to remove', { autoClose: 4000 });
      }
    } catch (e) {
      toast.error('Network error', { autoClose: 4000 });
    }
  };

  if (!effectivePropertyId) {
    return (
      <div className="stg-max-wide">
        <p className="stg-page-subtitle">Select a property to manage contractors.</p>
      </div>
    );
  }

  return (
    <div className="stg-max-wide">
      <div className="stg-page-header">
        <h1 className="stg-page-title">Contractors</h1>
        <p className="stg-page-subtitle">
          Add contractor companies to this property using their 6-digit company code.
          You can see what sections they have access to based on their company profile.
        </p>
      </div>

      <div className="stg-property-info">
        <strong>Property:</strong> {property?.name || 'Loading...'}
      </div>

      <div className="stg-section-header">
        <h2 className="stg-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Assigned Contractors {contractors.length > 0 && <span className="stg-count">{contractors.length}</span>}
        </h2>
        <button className="stg-btn stg-btn-primary stg-btn-sm" onClick={() => setShowAddModal(true)}>
          <FaPlus /> Add Contractor
        </button>
      </div>

      {loading ? (
        <div className="stg-loading">Loading...</div>
      ) : contractors.length === 0 ? (
        <div className="stg-empty">
          <FaHardHat className="stg-empty-icon" />
          <p>No contractors assigned to this property yet.</p>
          <button className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Add Contractor
          </button>
        </div>
      ) : (
        <div className="stg-entity-list">
          {contractors.map(c => (
            <div className="stg-entity-card" key={c.link_id}>
              <div className="stg-entity-info">
                <div className="stg-entity-avatar"><FaHardHat /></div>
                <div>
                  <h3 className="stg-entity-name">{c.company_name}</h3>
                  {c.service_type && <p className="stg-entity-detail" style={{ textTransform: 'capitalize' }}>{c.service_type.replace(/_/g, ' ')}</p>}
                  {c.admin_first_name && <p className="stg-entity-detail">Admin: {c.admin_first_name} {c.admin_last_name}</p>}
                  {c.company_email && <p className="stg-entity-detail">{c.company_email}</p>}
                  {c.sections && c.sections.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {c.sections.map(s => (
                        <span key={s} style={{
                          fontSize: '0.7rem', background: '#e0f2fe', color: '#0369a1',
                          padding: '2px 8px', borderRadius: 4, fontWeight: 500
                        }}>
                          {SECTION_LABELS[s] || s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                className="stg-remove-btn"
                onClick={() => handleRemove(c.link_id, c.company_name)}
                title="Remove"
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddContractorModal
          propertyId={effectivePropertyId}
          propertyName={property?.name || ''}
          onClose={() => setShowAddModal(false)}
          onAdded={fetchContractors}
        />
      )}
    </div>
  );
}

function AddContractorModal({ propertyId, propertyName, onClose, onAdded }) {
  const [companyCode, setCompanyCode] = useState('');
  const [step, setStep] = useState('enter');
  const [verified, setVerified] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    const code = companyCode.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setError('Company code must be exactly 6 digits');
      return;
    }
    setError('');
    setVerified(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/contractors/lookup?company_code=${encodeURIComponent(code)}`),
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.found) {
        setVerified(data);
        setStep('verify');
      } else {
        setError(data.message || 'No contractor company found with this code');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAdd = async (e) => {
    e.preventDefault();
    if (!verified) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/contractors`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ company_id: verified.company_id })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${verified.company_name} added to property`);
        onAdded();
        onClose();
      } else {
        setError(data.error || data.message || 'Failed to add contractor');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="stg-modal-overlay" onClick={onClose}>
      <div className="stg-modal" onClick={e => e.stopPropagation()}>
        <div className="stg-modal-header">
          <h3>{step === 'verify' ? 'Confirm contractor' : 'Add Contractor'}</h3>
          <button type="button" className="stg-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        {step === 'enter' ? (
          <form onSubmit={handleLookup}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Enter the contractor company&apos;s 6-digit company code. The company admin can find this in their dashboard.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">Company Code (6 digits) <span className="stg-required">*</span></label>
                <input
                  type="text"
                  className="stg-input"
                  placeholder="e.g. 380074"
                  value={companyCode}
                  onChange={e => setCompanyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Looking up\u2026' : 'Find Contractor'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmAdd}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Please verify this is the correct contractor company before granting access.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-verify-box">
                <p className="stg-verify-label">Company Name</p>
                <p className="stg-verify-value">{verified?.company_name}</p>
                {verified?.service_type && (
                  <>
                    <p className="stg-verify-label">Service Type</p>
                    <p className="stg-verify-value" style={{ textTransform: 'capitalize' }}>{verified.service_type.replace(/_/g, ' ')}</p>
                  </>
                )}
                {verified?.admin_name && (
                  <>
                    <p className="stg-verify-label">Company Admin</p>
                    <p className="stg-verify-value">{verified.admin_name}</p>
                  </>
                )}
                <p className="stg-verify-label">Company Code</p>
                <p className="stg-verify-value stg-verify-code">{verified?.company_code}</p>
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => { setStep('enter'); setVerified(null); setError(''); }}>Back</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Adding\u2026' : 'Confirm & grant access'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ContractorsSettings;
